/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/Sidebar';
import { useAuth, useTheme } from '../App';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { LayoutDashboard, Table2, KanbanSquare, CalendarRange, Target, Network } from 'lucide-react';

import DataView from './DataView';
import HomeView from '../components/tasks/HomeView';
import KanbanView from '../components/tasks/KanbanView';
import SprintsView from '../components/tasks/SprintsView';
import EpicView from '../components/tasks/EpicView';
import GraphView from '../components/tasks/GraphView';

const VALID_TABS = ['home', 'table', 'kanban', 'sprints', 'epic', 'graph'];
const DEFAULT_VIEW_KEY = 'orka.tasks.default_view';

const TasksPage = () => {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get('tab');
    if (fromUrl && VALID_TABS.includes(fromUrl)) return fromUrl;
    const saved = localStorage.getItem(DEFAULT_VIEW_KEY);
    if (saved && VALID_TABS.includes(saved)) return saved;
    return 'home';
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get('tab');
    if (fromUrl && VALID_TABS.includes(fromUrl) && fromUrl !== activeTab) {
      setActiveTab(fromUrl);
    }
  }, [location.search]);

  const switchTab = (val) => {
    setActiveTab(val);
    const params = new URLSearchParams(location.search);
    params.set('tab', val);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  // For Kanban / Sprints / Epic / Graph views, opening a task should navigate to Table tab
  // and emit a custom event. DataView listens to it to open the editor.
  const onOpenTask = (task) => {
    switchTab('table');
    // Allow DataView to mount, then dispatch event to open the editor
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('tasks:open', { detail: { task_id: task.task_id, task } }));
    }, 200);
  };

  return (
    <PageLayout title="Tasks" user={user} onLogout={logout} theme={theme} setTheme={setTheme}>
      <div data-testid="tasks-page">
        <Tabs value={activeTab} onValueChange={switchTab} className="w-full">
          <TabsList className="grid grid-cols-6 w-full max-w-3xl" data-testid="tasks-tabs">
            <TabsTrigger value="home" data-testid="tasks-tab-home"><LayoutDashboard className="w-3.5 h-3.5 mr-1.5" />Home</TabsTrigger>
            <TabsTrigger value="table" data-testid="tasks-tab-table"><Table2 className="w-3.5 h-3.5 mr-1.5" />Table</TabsTrigger>
            <TabsTrigger value="kanban" data-testid="tasks-tab-kanban"><KanbanSquare className="w-3.5 h-3.5 mr-1.5" />Kanban</TabsTrigger>
            <TabsTrigger value="sprints" data-testid="tasks-tab-sprints"><CalendarRange className="w-3.5 h-3.5 mr-1.5" />Sprints</TabsTrigger>
            <TabsTrigger value="epic" data-testid="tasks-tab-epic"><Target className="w-3.5 h-3.5 mr-1.5" />Epic</TabsTrigger>
            <TabsTrigger value="graph" data-testid="tasks-tab-graph"><Network className="w-3.5 h-3.5 mr-1.5" />Graph</TabsTrigger>
          </TabsList>

          <TabsContent value="home" className="mt-3">
            <HomeView onSwitchTab={switchTab} />
          </TabsContent>
          <TabsContent value="table" className="mt-3" forceMount hidden={activeTab !== 'table'}>
            {/* forceMount keeps DataView alive across tab switches so its state survives */}
            <DataView embedded />
          </TabsContent>
          <TabsContent value="kanban" className="mt-3">
            <KanbanView onOpenTask={onOpenTask} />
          </TabsContent>
          <TabsContent value="sprints" className="mt-3">
            <SprintsView onOpenTask={onOpenTask} />
          </TabsContent>
          <TabsContent value="epic" className="mt-3">
            <EpicView onOpenTask={onOpenTask} />
          </TabsContent>
          <TabsContent value="graph" className="mt-3">
            <GraphView onOpenTask={onOpenTask} />
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  );
};

export default TasksPage;
